import type {
  LayerStyles,
  DropShadowEffect,
  ColorOverlayEffect,
  StrokeEffect,
  InnerShadowEffect,
  OuterGlowEffect,
  InnerGlowEffect,
  BevelEmbossEffect,
  SatinEffect,
  GradientOverlayEffect,
  PatternOverlayEffect,
  ContourSpec,
  GlowFill,
} from "../document/LayerStyle";
import type { BlendMode } from "../document/Layer";
import { nativeCompositeOp, compositeLayer } from "../pixel/blend";

// ---- Alpha mask utilities ----

function extractAlpha(
  source: HTMLCanvasElement,
  width: number,
  height: number,
): Float32Array {
  const ctx = source.getContext("2d")!;
  const data = ctx.getImageData(0, 0, width, height).data;
  const alpha = new Float32Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const a = data[i * 4 + 3];
    alpha[i] = a !== undefined ? a / 255 : 0;
  }
  return alpha;
}

function alphaToCanvas(
  alpha: Float32Array,
  width: number,
  height: number,
  color: { r: number; g: number; b: number; a: number },
  opacity: number,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  const imageData = ctx.createImageData(width, height);
  const oa = opacity * (color.a / 255);
  for (let i = 0; i < width * height; i++) {
    const av = alpha[i] ?? 0;
    const a = Math.round(av * oa * 255);
    const idx = i * 4;
    imageData.data[idx] = color.r;
    imageData.data[idx + 1] = color.g;
    imageData.data[idx + 2] = color.b;
    imageData.data[idx + 3] = a;
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

// ---- Separable morphological operations (dilate / erode on Float32Array) ----

export function dilate(
  alpha: Float32Array,
  width: number,
  height: number,
  radius: number,
): Float32Array {
  if (radius <= 0) return new Float32Array(alpha);
  const r = Math.ceil(radius);
  const horiz = new Float32Array(width * height);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let max = 0;
      const x1 = Math.max(0, x - r);
      const x2 = Math.min(width - 1, x + r);
      for (let sx = x1; sx <= x2; sx++) {
        const v = alpha[y * width + sx] ?? 0;
        if (v > max) max = v;
      }
      horiz[y * width + x] = max;
    }
  }

  const result = new Float32Array(width * height);
  for (let y = 0; y < height; y++) {
    const y1 = Math.max(0, y - r);
    const y2 = Math.min(height - 1, y + r);
    for (let x = 0; x < width; x++) {
      let max = 0;
      for (let sy = y1; sy <= y2; sy++) {
        const v = horiz[sy * width + x] ?? 0;
        if (v > max) max = v;
      }
      result[y * width + x] = max;
    }
  }
  return result;
}

export function erode(
  alpha: Float32Array,
  width: number,
  height: number,
  radius: number,
): Float32Array {
  if (radius <= 0) return new Float32Array(alpha);
  const r = Math.ceil(radius);
  const horiz = new Float32Array(width * height);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let min = 1;
      const x1 = Math.max(0, x - r);
      const x2 = Math.min(width - 1, x + r);
      for (let sx = x1; sx <= x2; sx++) {
        const v = alpha[y * width + sx] ?? 0;
        if (v < min) min = v;
      }
      horiz[y * width + x] = min;
    }
  }

  const result = new Float32Array(width * height);
  for (let y = 0; y < height; y++) {
    const y1 = Math.max(0, y - r);
    const y2 = Math.min(height - 1, y + r);
    for (let x = 0; x < width; x++) {
      let min = 1;
      for (let sy = y1; sy <= y2; sy++) {
        const v = horiz[sy * width + x] ?? 0;
        if (v < min) min = v;
      }
      result[y * width + x] = min;
    }
  }
  return result;
}

// ---- Box blur on Float32Array (3-pass for better Gaussian approximation) ----

export function boxBlur(
  alpha: Float32Array,
  width: number,
  height: number,
  radius: number,
): Float32Array {
  if (radius <= 0) return new Float32Array(alpha);
  let result = new Float32Array(alpha);
  const passes = 3;
  const r = Math.max(1, Math.round(radius / passes));

  for (let pass = 0; pass < passes; pass++) {
    const input = result;
    const output = new Float32Array(width * height);

    for (let y = 0; y < height; y++) {
      let sum = 0;
      for (let x = 0; x < width; x++) {
        sum += input[y * width + x] ?? 0;
        if (x > r) {
          sum -= input[y * width + (x - r - 1)] ?? 0;
        }
        const count = Math.min(x + 1, r + 1);
        output[y * width + x] = sum / count;
      }
    }

    const temp = new Float32Array(width * height);
    for (let x = 0; x < width; x++) {
      let sum = 0;
      for (let y = 0; y < height; y++) {
        sum += output[y * width + x] ?? 0;
        if (y > r) {
          sum -= output[(y - r - 1) * width + x] ?? 0;
        }
        const count = Math.min(y + 1, r + 1);
        temp[y * width + x] = sum / count;
      }
    }

    result = temp;
  }

  return result;
}

// ---- Contour evaluation ----

export function evaluateContour(contour: ContourSpec, t: number): number {
  const points = contour.points;
  if (points.length === 0) return t;
  if (points.length === 1) return points[0]!.y;

  t = Math.max(0, Math.min(1, t));

  if (t <= points[0]!.x) return points[0]!.y;
  if (t >= points[points.length - 1]!.x) return points[points.length - 1]!.y;

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i]!;
    const p1 = points[i + 1]!;
    if (t >= p0.x && t <= p1.x) {
      const range = p1.x - p0.x;
      if (range === 0) return p0.y;
      const frac = (t - p0.x) / range;
      return p0.y + frac * (p1.y - p0.y);
    }
  }

  return points[points.length - 1]!.y;
}

export function applyContour(
  alpha: Float32Array,
  width: number,
  height: number,
  contour: ContourSpec,
  range: number,
): Float32Array {
  const result = new Float32Array(width * height);
  const rangeStart = 1 - range;
  for (let i = 0; i < width * height; i++) {
    const v = alpha[i] ?? 0;
    const t = Math.max(0, Math.min(1, (v - rangeStart) / range));
    result[i] = evaluateContour(contour, t);
  }
  return result;
}

// ---- Composite helper (handles native and offscreen blend modes) ----

function compositeOnCanvas(
  ctx: CanvasRenderingContext2D,
  src: HTMLCanvasElement,
  blendMode: BlendMode,
  opacity: number,
  dx: number,
  dy: number,
): void {
  const native = nativeCompositeOp(blendMode);
  if (native !== null) {
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.globalCompositeOperation = native;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(src, dx, dy);
    ctx.restore();
    return;
  }

  const w = src.width;
  const h = src.height;
  if (w <= 0 || h <= 0) return;

  const dstData = ctx.getImageData(dx, dy, w, h);
  const sctx = src.getContext("2d")!;
  const srcData = sctx.getImageData(0, 0, w, h);

  compositeLayer(dstData.data, srcData.data, blendMode, opacity);

  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  ctx.putImageData(dstData, dx, dy);
  ctx.restore();
}

// ---- Drop Shadow ----

function renderDropShadow(
  ctx: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
  style: DropShadowEffect,
  width: number,
  height: number,
): void {
  const { color, opacity, angle, distance, size, spread } = style;
  if (size <= 0 && spread <= 0 && distance <= 0) return;

  const alpha = extractAlpha(source, width, height);
  const shadowAlpha = spread > 0 ? dilate(alpha, width, height, spread) : alpha;
  const shadowCanvas = alphaToCanvas(shadowAlpha, width, height, color, 1);

  const angleRad = (angle * Math.PI) / 180;
  const dx = distance * Math.cos(angleRad);
  const dy = distance * Math.sin(angleRad);

  if (size > 0 || distance > 0) {
    const pad = Math.ceil(size + Math.abs(dx) + Math.abs(dy));
    const blurCanvas = document.createElement("canvas");
    blurCanvas.width = width + pad * 2;
    blurCanvas.height = height + pad * 2;
    const bctx = blurCanvas.getContext("2d")!;

    bctx.shadowColor = `rgba(${color.r},${color.g},${color.b},${opacity})`;
    bctx.shadowBlur = size;
    bctx.shadowOffsetX = dx;
    bctx.shadowOffsetY = dy;
    bctx.drawImage(shadowCanvas, pad, pad);

    compositeOnCanvas(ctx, blurCanvas, style.blendMode, 1, -pad, -pad);
  } else {
    compositeOnCanvas(ctx, shadowCanvas, style.blendMode, opacity, 0, 0);
  }
}

// ---- Color Overlay ----

function renderColorOverlay(
  ctx: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
  style: ColorOverlayEffect,
  width: number,
  height: number,
): void {
  const alpha = extractAlpha(source, width, height);
  const overlayCanvas = alphaToCanvas(alpha, width, height, style.color, style.opacity);
  compositeOnCanvas(ctx, overlayCanvas, style.blendMode, 1, 0, 0);
}

// ---- Stroke ----

function renderStroke(
  ctx: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
  style: StrokeEffect,
  width: number,
  height: number,
): void {
  if (!style.size || style.size <= 0) return;
  const color = style.color;
  if (!color) return;

  const alpha = extractAlpha(source, width, height);
  const { size, position, opacity, blendMode } = style;

  let strokeAlpha: Float32Array;
  if (position === "outside") {
    const expanded = dilate(alpha, width, height, size);
    strokeAlpha = new Float32Array(width * height);
    for (let i = 0; i < width * height; i++) {
      const ea = expanded[i] ?? 0;
      const aa = alpha[i] ?? 0;
      strokeAlpha[i] = Math.max(0, ea - aa);
    }
  } else if (position === "inside") {
    const eroded = erode(alpha, width, height, size);
    strokeAlpha = new Float32Array(width * height);
    for (let i = 0; i < width * height; i++) {
      const aa = alpha[i] ?? 0;
      const ea = eroded[i] ?? 0;
      strokeAlpha[i] = Math.max(0, aa - ea);
    }
  } else {
    const halfSize = Math.ceil(size / 2);
    const expanded = dilate(alpha, width, height, halfSize);
    const eroded = erode(alpha, width, height, size - halfSize);
    strokeAlpha = new Float32Array(width * height);
    for (let i = 0; i < width * height; i++) {
      const ea = expanded[i] ?? 0;
      const aa = alpha[i] ?? 0;
      const er = eroded[i] ?? 0;
      const outer = Math.max(0, ea - aa);
      const inner = Math.max(0, aa - er);
      strokeAlpha[i] = Math.max(outer, inner);
    }
  }

  const strokeCanvas = alphaToCanvas(strokeAlpha, width, height, color, opacity);
  compositeOnCanvas(ctx, strokeCanvas, blendMode, 1, 0, 0);
}

// ---- Inner Shadow ----

function renderInnerShadow(
  ctx: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
  style: InnerShadowEffect,
  width: number,
  height: number,
): void {
  const { color, opacity, angle, distance, size, choke } = style;
  if (size <= 0 && choke <= 0 && distance <= 0) return;

  const alpha = extractAlpha(source, width, height);
  let shadowAlpha = choke > 0 ? erode(alpha, width, height, choke) : alpha;

  const angleRad = (angle * Math.PI) / 180;
  const dx = distance * Math.cos(angleRad);
  const dy = distance * Math.sin(angleRad);

  if (size > 0 || distance > 0) {
    const pad = Math.ceil(size + Math.abs(dx) + Math.abs(dy));
    const blurCanvas = document.createElement("canvas");
    blurCanvas.width = width + pad * 2;
    blurCanvas.height = height + pad * 2;
    const bctx = blurCanvas.getContext("2d")!;

    bctx.shadowColor = `rgba(${color.r},${color.g},${color.b},${opacity})`;
    bctx.shadowBlur = size;
    bctx.shadowOffsetX = dx;
    bctx.shadowOffsetY = dy;

    const shadowCanvas = alphaToCanvas(shadowAlpha, width, height, color, 1);
    bctx.drawImage(shadowCanvas, pad, pad);

    const clipped = document.createElement("canvas");
    clipped.width = width;
    clipped.height = height;
    const cctx = clipped.getContext("2d")!;
    cctx.drawImage(blurCanvas, -pad, -pad);

    cctx.globalCompositeOperation = "destination-in";
    cctx.drawImage(source, 0, 0);

    compositeOnCanvas(ctx, clipped, style.blendMode, 1, 0, 0);
  } else {
    const innerCanvas = alphaToCanvas(shadowAlpha, width, height, color, opacity);
    const clipped = document.createElement("canvas");
    clipped.width = width;
    clipped.height = height;
    const cctx = clipped.getContext("2d")!;
    cctx.drawImage(innerCanvas, 0, 0);
    cctx.globalCompositeOperation = "destination-in";
    cctx.drawImage(source, 0, 0);
    compositeOnCanvas(ctx, clipped, style.blendMode, 1, 0, 0);
  }
}

// ---- Glow fill color helper ----

function getGlowColor(fill: GlowFill): { r: number; g: number; b: number; a: number } {
  if (fill.kind === "color") {
    return fill.color;
  }
  return { r: 255, g: 255, b: 255, a: 255 };
}

function applyGlowContour(
  alpha: Float32Array,
  width: number,
  height: number,
  contour: ContourSpec,
  range: number,
): Float32Array {
  return applyContour(alpha, width, height, contour, range);
}

// ---- Outer Glow ----

function renderOuterGlow(
  ctx: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
  style: OuterGlowEffect,
  width: number,
  height: number,
): void {
  const { opacity, size, spread, contour, range, jitter, fill } = style;
  if (size <= 0 && spread <= 0) return;

  const alpha = extractAlpha(source, width, height);
  let glowAlpha = dilate(alpha, width, height, spread);

  if (size > 0) {
    glowAlpha = boxBlur(glowAlpha, width, height, size);
  }

  if (jitter > 0) {
    for (let i = 0; i < glowAlpha.length; i++) {
      const noise = (Math.random() - 0.5) * 2 * jitter;
      glowAlpha[i] = Math.max(0, Math.min(1, (glowAlpha[i] ?? 0) + noise));
    }
  }

  glowAlpha = applyGlowContour(glowAlpha, width, height, contour, range);

  const glowColor = getGlowColor(fill);
  const glowCanvas = alphaToCanvas(glowAlpha, width, height, glowColor, 1);

  if (size > 0) {
    const pad = Math.ceil(size);
    const blurCanvas = document.createElement("canvas");
    blurCanvas.width = width + pad * 2;
    blurCanvas.height = height + pad * 2;
    const bctx = blurCanvas.getContext("2d")!;

    const colorStr =
      fill.kind === "color"
        ? `rgba(${glowColor.r},${glowColor.g},${glowColor.b},${opacity})`
        : `rgba(255,255,255,${opacity})`;
    bctx.shadowColor = colorStr;
    bctx.shadowBlur = size;
    bctx.drawImage(glowCanvas, pad, pad);

    compositeOnCanvas(ctx, blurCanvas, style.blendMode, 1, -pad, -pad);
  } else {
    compositeOnCanvas(ctx, glowCanvas, style.blendMode, opacity, 0, 0);
  }
}

// ---- Inner Glow ----

function renderInnerGlow(
  ctx: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
  style: InnerGlowEffect,
  width: number,
  height: number,
): void {
  const { opacity, size, choke, contour, range, jitter, fill, source: glowSource } = style;
  if (size <= 0 && choke <= 0) return;

  const alpha = extractAlpha(source, width, height);
  let glowAlpha: Float32Array;

  if (glowSource === "center") {
    glowAlpha = erode(alpha, width, height, choke);
    if (size > 0) {
      glowAlpha = boxBlur(glowAlpha, width, height, size);
    }
    glowAlpha = applyGlowContour(glowAlpha, width, height, contour, range);
  } else {
    const eroded = choke > 0 ? erode(alpha, width, height, choke) : alpha;
    glowAlpha = new Float32Array(width * height);
    for (let i = 0; i < width * height; i++) {
      const aa = alpha[i] ?? 0;
      const ea = eroded[i] ?? 0;
      glowAlpha[i] = Math.max(0, aa - ea);
    }
    if (size > 0) {
      glowAlpha = boxBlur(glowAlpha, width, height, size);
    }
    glowAlpha = applyGlowContour(glowAlpha, width, height, contour, range);
  }

  if (jitter > 0) {
    for (let i = 0; i < glowAlpha.length; i++) {
      const noise = (Math.random() - 0.5) * 2 * jitter;
      glowAlpha[i] = Math.max(0, Math.min(1, (glowAlpha[i] ?? 0) + noise));
    }
  }

  const glowColor = getGlowColor(fill);
  const glowCanvas = alphaToCanvas(glowAlpha, width, height, glowColor, 1);

  const clipped = document.createElement("canvas");
  clipped.width = width;
  clipped.height = height;
  const cctx = clipped.getContext("2d")!;
  cctx.drawImage(glowCanvas, 0, 0);
  cctx.globalCompositeOperation = "destination-in";
  cctx.drawImage(source, 0, 0);

  if (size > 0) {
    const pad = Math.ceil(size);
    const blurCanvas = document.createElement("canvas");
    blurCanvas.width = width + pad * 2;
    blurCanvas.height = height + pad * 2;
    const bctx = blurCanvas.getContext("2d")!;

    const colorStr =
      fill.kind === "color"
        ? `rgba(${glowColor.r},${glowColor.g},${glowColor.b},${opacity})`
        : `rgba(255,255,255,${opacity})`;
    bctx.shadowColor = colorStr;
    bctx.shadowBlur = size;
    bctx.drawImage(clipped, pad, pad);

    compositeOnCanvas(ctx, blurCanvas, style.blendMode, 1, -pad, -pad);
  } else {
    compositeOnCanvas(ctx, clipped, style.blendMode, opacity, 0, 0);
  }
}

// ---- Satin ----

function renderSatin(
  ctx: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
  style: SatinEffect,
  width: number,
  height: number,
): void {
  const { color, opacity, angle, distance, size, contour, invert } = style;
  if (size <= 0 && distance <= 0) return;

  const alpha = extractAlpha(source, width, height);

  const angleRad = (angle * Math.PI) / 180;
  const dx = Math.round(distance * Math.cos(angleRad));
  const dy = Math.round(distance * Math.sin(angleRad));

  const shifted = new Float32Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const sx = x - dx;
      const sy = y - dy;
      if (sx >= 0 && sx < width && sy >= 0 && sy < height) {
        shifted[y * width + x] = alpha[sy * width + sx] ?? 0;
      }
    }
  }

  const diff = new Float32Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const a = alpha[i] ?? 0;
    const b = shifted[i] ?? 0;
    diff[i] = Math.abs(a - b);
  }

  let satinAlpha = boxBlur(diff, width, height, size);

  satinAlpha = applyContour(satinAlpha, width, height, contour, 1);

  if (invert) {
    for (let i = 0; i < satinAlpha.length; i++) {
      satinAlpha[i] = 1 - (satinAlpha[i] ?? 0);
    }
  }

  const satinCanvas = alphaToCanvas(satinAlpha, width, height, color, 1);

  const clipped = document.createElement("canvas");
  clipped.width = width;
  clipped.height = height;
  const cctx = clipped.getContext("2d")!;
  cctx.drawImage(satinCanvas, 0, 0);
  cctx.globalCompositeOperation = "destination-in";
  cctx.drawImage(source, 0, 0);

  compositeOnCanvas(ctx, clipped, style.blendMode, opacity, 0, 0);
}

// ---- Bevel & Emboss ----

function renderBevelEmboss(
  ctx: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
  style: BevelEmbossEffect,
  width: number,
  height: number,
): void {
  const {
    style: bevelStyle,
    technique,
    depth,
    direction,
    size,
    soften,
    angle,
    altitude,
    glossContour,
    highlightBlendMode,
    highlightColor,
    highlightOpacity,
    shadowBlendMode,
    shadowColor,
    shadowOpacity,
  } = style;

  if (size <= 0) return;

  const alpha = extractAlpha(source, width, height);

  const lightAngle = (angle * Math.PI) / 180;
  const lightDist = size * depth * (direction === "up" ? 1 : -1);
  const ldx = lightDist * Math.cos(lightAngle);
  const ldy = lightDist * Math.sin(lightAngle);

  let highlightAlpha: Float32Array;
  let shadowAlpha: Float32Array;

  if (bevelStyle === "outer-bevel") {
    const expanded = dilate(alpha, width, height, size);
    highlightAlpha = new Float32Array(width * height);
    shadowAlpha = new Float32Array(width * height);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const sx = Math.round(x - ldx);
        const sy = Math.round(y - ldy);
        const shiftedAlpha =
          sx >= 0 && sx < width && sy >= 0 && sy < height
            ? alpha[sy * width + sx] ?? 0
            : 0;
        const ea = expanded[y * width + x] ?? 0;
        const aa = alpha[y * width + x] ?? 0;
        const outer = Math.max(0, ea - aa);
        highlightAlpha[y * width + x] = outer * Math.max(0, shiftedAlpha - aa);
        shadowAlpha[y * width + x] = outer * Math.max(0, aa - shiftedAlpha);
      }
    }
  } else if (bevelStyle === "emboss") {
    highlightAlpha = new Float32Array(width * height);
    shadowAlpha = new Float32Array(width * height);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const sx = Math.round(x - ldx);
        const sy = Math.round(y - ldy);
        const shiftedAlpha =
          sx >= 0 && sx < width && sy >= 0 && sy < height
            ? alpha[sy * width + sx] ?? 0
            : 0;
        const aa = alpha[y * width + x] ?? 0;
        highlightAlpha[y * width + x] = Math.max(0, shiftedAlpha - aa);
        shadowAlpha[y * width + x] = Math.max(0, aa - shiftedAlpha);
      }
    }
  } else if (bevelStyle === "pillow-emboss") {
    const expanded = dilate(alpha, width, height, size);
    // Hoist erode() out of the per-pixel loop (fix #195 — was O(w²·h²)).
    const eroded = erode(alpha, width, height, size);
    highlightAlpha = new Float32Array(width * height);
    shadowAlpha = new Float32Array(width * height);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const sx = Math.round(x - ldx);
        const sy = Math.round(y - ldy);
        const shiftedAlpha =
          sx >= 0 && sx < width && sy >= 0 && sy < height
            ? alpha[sy * width + sx] ?? 0
            : 0;
        const ea = expanded[y * width + x] ?? 0;
        const aa = alpha[y * width + x] ?? 0;
        const outer = Math.max(0, ea - aa);
        const inner = Math.max(0, aa - (eroded[y * width + x] ?? 0));
        highlightAlpha[y * width + x] =
          outer * Math.max(0, shiftedAlpha - aa) +
          inner * Math.max(0, aa - shiftedAlpha);
        shadowAlpha[y * width + x] =
          outer * Math.max(0, aa - shiftedAlpha) +
          inner * Math.max(0, shiftedAlpha - aa);
      }
    }
  } else {
    const eroded = erode(alpha, width, height, size);
    highlightAlpha = new Float32Array(width * height);
    shadowAlpha = new Float32Array(width * height);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const sx = Math.round(x - ldx);
        const sy = Math.round(y - ldy);
        const shiftedAlpha =
          sx >= 0 && sx < width && sy >= 0 && sy < height
            ? alpha[sy * width + sx] ?? 0
            : 0;
        const aa = alpha[y * width + x] ?? 0;
        const ea = eroded[y * width + x] ?? 0;
        const inner = Math.max(0, aa - ea);
        highlightAlpha[y * width + x] = inner * Math.max(0, shiftedAlpha - ea);
        shadowAlpha[y * width + x] = inner * Math.max(0, ea - shiftedAlpha);
      }
    }
  }

  if (soften > 0) {
    highlightAlpha = boxBlur(highlightAlpha, width, height, soften);
    shadowAlpha = boxBlur(shadowAlpha, width, height, soften);
  }

  highlightAlpha = applyContour(highlightAlpha, width, height, glossContour, 1);
  shadowAlpha = applyContour(shadowAlpha, width, height, glossContour, 1);

  const highlightCanvas = alphaToCanvas(
    highlightAlpha,
    width,
    height,
    highlightColor,
    highlightOpacity,
  );
  const shadowCanvas = alphaToCanvas(
    shadowAlpha,
    width,
    height,
    shadowColor,
    shadowOpacity,
  );

  compositeOnCanvas(ctx, shadowCanvas, shadowBlendMode, 1, 0, 0);
  compositeOnCanvas(ctx, highlightCanvas, highlightBlendMode, 1, 0, 0);
}

// ---- Gradient Overlay ----

function renderGradientOverlay(
  ctx: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
  style: GradientOverlayEffect,
  width: number,
  height: number,
): void {
  const { gradient, opacity, reverse, style: gradientStyle, angle, scale } = style;

  const gradCanvas = document.createElement("canvas");
  gradCanvas.width = width;
  gradCanvas.height = height;
  const gctx = gradCanvas.getContext("2d")!;

  const cx = width / 2;
  const cy = height / 2;
  const maxDim = Math.max(width, height) * scale;

  let grad: CanvasGradient;
  const angleRad = (angle * Math.PI) / 180;
  const halfLen = maxDim / 2;
  const x1 = cx - Math.cos(angleRad) * halfLen;
  const y1 = cy - Math.sin(angleRad) * halfLen;
  const x2 = cx + Math.cos(angleRad) * halfLen;
  const y2 = cy + Math.sin(angleRad) * halfLen;

  switch (gradientStyle) {
    case "linear":
      grad = gctx.createLinearGradient(x1, y1, x2, y2);
      break;
    case "radial":
      grad = gctx.createRadialGradient(cx, cy, 0, cx, cy, maxDim / 2);
      break;
    case "angle": {
      const steps = 36;
      grad = gctx.createConicGradient(-angleRad + Math.PI / 2, cx, cy);
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const pos = reverse ? 1 - t : t;
        const color = sampleGradient(gradient, pos);
        grad.addColorStop(t, `rgba(${color.r},${color.g},${color.b},${color.a / 255})`);
      }
      gctx.fillStyle = grad;
      gctx.fillRect(0, 0, width, height);
      const clipped = document.createElement("canvas");
      clipped.width = width;
      clipped.height = height;
      const cctx = clipped.getContext("2d")!;
      cctx.drawImage(gradCanvas, 0, 0);
      cctx.globalCompositeOperation = "destination-in";
      cctx.drawImage(source, 0, 0);
      compositeOnCanvas(ctx, clipped, style.blendMode, opacity, 0, 0);
      return;
    }
    case "reflected": {
      grad = gctx.createLinearGradient(x1, y1, x2, y2);
      const stops = gradient.colorStops;
      for (let i = 0; i < stops.length; i++) {
        const stop = stops[i]!;
        const pos = reverse ? 1 - stop.position : stop.position;
        const mirrored = 0.5 + (pos - 0.5) * 2;
        const color = sampleGradient(gradient, pos);
        grad.addColorStop(Math.max(0, Math.min(1, mirrored)), `rgba(${color.r},${color.g},${color.b},${color.a / 255})`);
      }
      break;
    }
    case "diamond":
      grad = gctx.createRadialGradient(cx, cy, 0, cx, cy, maxDim / 2);
      break;
    default:
      grad = gctx.createLinearGradient(x1, y1, x2, y2);
  }

  const stops = gradient.colorStops;
  for (let i = 0; i < stops.length; i++) {
    const stop = stops[i]!;
    const pos = reverse ? 1 - stop.position : stop.position;
    const color = sampleGradient(gradient, pos);
    grad.addColorStop(pos, `rgba(${color.r},${color.g},${color.b},${color.a / 255})`);
  }

  gctx.fillStyle = grad;
  gctx.fillRect(0, 0, width, height);

  const alpha = extractAlpha(source, width, height);
  const clipped = alphaToCanvas(alpha, width, height, { r: 255, g: 255, b: 255, a: 255 }, 1);
  const finalCanvas = document.createElement("canvas");
  finalCanvas.width = width;
  finalCanvas.height = height;
  const fctx = finalCanvas.getContext("2d")!;
  fctx.drawImage(gradCanvas, 0, 0);
  fctx.globalCompositeOperation = "destination-in";
  fctx.drawImage(clipped, 0, 0);

  compositeOnCanvas(ctx, finalCanvas, style.blendMode, opacity, 0, 0);
}

export function sampleGradient(
  gradient: { readonly colorStops: ReadonlyArray<{ position: number; color: { r: number; g: number; b: number; a: number } }> },
  position: number,
): { r: number; g: number; b: number; a: number } {
  const stops = gradient.colorStops;
  if (stops.length === 0) return { r: 0, g: 0, b: 0, a: 255 };
  if (stops.length === 1) return stops[0]!.color;

  position = Math.max(0, Math.min(1, position));

  if (position <= stops[0]!.position) return stops[0]!.color;
  if (position >= stops[stops.length - 1]!.position) return stops[stops.length - 1]!.color;

  for (let i = 0; i < stops.length - 1; i++) {
    const s0 = stops[i]!;
    const s1 = stops[i + 1]!;
    if (position >= s0.position && position <= s1.position) {
      const range = s1.position - s0.position;
      if (range === 0) return s0.color;
      const t = (position - s0.position) / range;
      return {
        r: Math.round(s0.color.r + t * (s1.color.r - s0.color.r)),
        g: Math.round(s0.color.g + t * (s1.color.g - s0.color.g)),
        b: Math.round(s0.color.b + t * (s1.color.b - s0.color.b)),
        a: Math.round(s0.color.a + t * (s1.color.a - s0.color.a)),
      };
    }
  }

  return stops[stops.length - 1]!.color;
}

// ---- Pattern Overlay ----

/**
 * Render a Pattern Overlay effect.  When `patternResolver` can supply pixel
 * data for the style's `pattern.id`, it is tiled via `ctx.createPattern` with
 * optional scale and phase; otherwise the legacy 8×8 checkerboard placeholder
 * is used so the effect remains visible in the UI (fixes #168).
 */
function renderPatternOverlay(
  ctx: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
  style: PatternOverlayEffect,
  width: number,
  height: number,
  patternResolver?: ((id: string) => HTMLCanvasElement | null) | null,
): void {
  const patternCanvas = document.createElement("canvas");
  patternCanvas.width = width;
  patternCanvas.height = height;
  const pctx = patternCanvas.getContext("2d")!;

  // Try to resolve the actual pattern tile from the resolver.
  const resolvedTile = patternResolver ? patternResolver(style.pattern.id) : null;

  if (resolvedTile && resolvedTile.width > 0 && resolvedTile.height > 0) {
    // scale is 0..1, where 1 = 100% of the pattern's native size.
    const scale = Math.max(0.01, style.scale);
    const tileW = Math.max(1, Math.round(resolvedTile.width * scale));
    const tileH = Math.max(1, Math.round(resolvedTile.height * scale));

    // Draw scaled tile into an intermediate canvas for createPattern.
    const tileCanvas = document.createElement("canvas");
    tileCanvas.width = tileW;
    tileCanvas.height = tileH;
    const tctx = tileCanvas.getContext("2d")!;
    tctx.drawImage(resolvedTile, 0, 0, resolvedTile.width, resolvedTile.height, 0, 0, tileW, tileH);

    const phaseX = style.phase?.x ?? 0;
    const phaseY = style.phase?.y ?? 0;

    const pattern = pctx.createPattern(tileCanvas, "repeat");
    if (pattern) {
      if (phaseX !== 0 || phaseY !== 0) {
        pctx.save();
        pctx.translate(phaseX, phaseY);
        pctx.fillStyle = pattern;
        // Extend fill rect to cover area revealed by translate.
        pctx.fillRect(-phaseX, -phaseY, width + tileW, height + tileH);
        pctx.restore();
      } else {
        pctx.fillStyle = pattern;
        pctx.fillRect(0, 0, width, height);
      }
    }
  } else {
    // Checkerboard fallback — visible in UI when no pattern data is available.
    const tileSize = 8;
    for (let y = 0; y < height; y += tileSize) {
      for (let x = 0; x < width; x += tileSize) {
        const isLight = ((Math.floor(x / tileSize) + Math.floor(y / tileSize)) % 2) === 0;
        pctx.fillStyle = isLight ? "rgba(200,200,200,0.3)" : "rgba(150,150,150,0.3)";
        pctx.fillRect(x, y, tileSize, tileSize);
      }
    }
  }

  const alpha = extractAlpha(source, width, height);
  const clipped = alphaToCanvas(alpha, width, height, { r: 255, g: 255, b: 255, a: 255 }, 1);
  const finalCanvas = document.createElement("canvas");
  finalCanvas.width = width;
  finalCanvas.height = height;
  const fctx = finalCanvas.getContext("2d")!;
  fctx.drawImage(patternCanvas, 0, 0);
  fctx.globalCompositeOperation = "destination-in";
  fctx.drawImage(clipped, 0, 0);

  compositeOnCanvas(ctx, finalCanvas, style.blendMode, style.opacity, 0, 0);
}

// ---- Style presence check ----

export function hasAnyStyle(styles: LayerStyles): boolean {
  return !!(
    styles.dropShadow?.enabled ||
    styles.innerShadow?.enabled ||
    styles.outerGlow?.enabled ||
    styles.innerGlow?.enabled ||
    styles.bevelEmboss?.enabled ||
    styles.satin?.enabled ||
    styles.colorOverlay?.enabled ||
    styles.gradientOverlay?.enabled ||
    styles.patternOverlay?.enabled ||
    styles.stroke?.enabled
  );
}

// ---- Main entry point ----

export function renderLayerStyles(
  source: HTMLCanvasElement | ImageData,
  styles: LayerStyles,
  width: number,
  height: number,
  patternResolver?: ((id: string) => HTMLCanvasElement | null) | null,
  /**
   * Optional alternative canvas to draw as the layer's own pixel content
   * (step 3 — "Draw source layer content").  When supplied, this canvas is
   * drawn instead of `source`, allowing the caller to bake in fill-opacity
   * while still using the full-alpha `source` for effect mask extraction.
   * Fixes the double-draw + blend-mode issue (#193).
   */
  fillSource?: HTMLCanvasElement | null,
): HTMLCanvasElement {
  const sourceCanvas: HTMLCanvasElement =
    source instanceof HTMLCanvasElement
      ? source
      : (() => {
          const c = document.createElement("canvas");
          c.width = width;
          c.height = height;
          const cx = c.getContext("2d")!;
          cx.putImageData(source, 0, 0);
          return c;
        })();

  // The canvas drawn as the actual layer pixel content at step 3.
  // Defaults to sourceCanvas when no fillSource override is given.
  const pixelCanvas = fillSource ?? sourceCanvas;

  const output = document.createElement("canvas");
  output.width = width;
  output.height = height;
  const ctx = output.getContext("2d")!;

  // Photoshop layer effects rendering order:
  // 1. Drop Shadow (outside layer)
  if (styles.dropShadow?.enabled) {
    renderDropShadow(ctx, sourceCanvas, styles.dropShadow, width, height);
  }

  if (styles.dropShadow?.enabled && styles.dropShadow.layerKnocksOut) {
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    ctx.drawImage(sourceCanvas, 0, 0);
    ctx.restore();
  }

  // 2. Outer Glow (outside layer)
  if (styles.outerGlow?.enabled) {
    renderOuterGlow(ctx, sourceCanvas, styles.outerGlow, width, height);
  }

  // 3. Bevel & Emboss (before inner effects)
  if (styles.bevelEmboss?.enabled) {
    renderBevelEmboss(ctx, sourceCanvas, styles.bevelEmboss, width, height);
  }

  // Draw layer pixel content (uses fillSource when provided, so fill-opacity
  // is applied to pixels without affecting the effect alpha masks).
  ctx.drawImage(pixelCanvas, 0, 0);

  // 4. Inner Shadow (inside layer)
  if (styles.innerShadow?.enabled) {
    renderInnerShadow(ctx, sourceCanvas, styles.innerShadow, width, height);
  }

  // 5. Inner Glow (inside layer)
  if (styles.innerGlow?.enabled) {
    renderInnerGlow(ctx, sourceCanvas, styles.innerGlow, width, height);
  }

  // 6. Satin (inside layer)
  if (styles.satin?.enabled) {
    renderSatin(ctx, sourceCanvas, styles.satin, width, height);
  }

  // 7. Color Overlay (fills layer)
  if (styles.colorOverlay?.enabled) {
    renderColorOverlay(ctx, sourceCanvas, styles.colorOverlay, width, height);
  }

  // 8. Gradient Overlay (fills layer)
  if (styles.gradientOverlay?.enabled) {
    renderGradientOverlay(ctx, sourceCanvas, styles.gradientOverlay, width, height);
  }

  // 9. Pattern Overlay (fills layer)
  if (styles.patternOverlay?.enabled) {
    renderPatternOverlay(ctx, sourceCanvas, styles.patternOverlay, width, height, patternResolver);
  }

  // 10. Stroke (outside/inside/center)
  if (styles.stroke?.enabled) {
    renderStroke(ctx, sourceCanvas, styles.stroke, width, height);
  }

  return output;
}
